<template>
	<BlockVisibilityObserver title="FINAL_CTA">
		<section class="kova-v2-final">
			<div class="kova-v2-final__copy">
				<h2 class="kova-v2-final__title">
					<span
						v-for="(part, index) in titleParts"
						:key="index"
						:class="{ 'kova-v2-final__title-accent': part.accent }"
						v-text="part.text"
					/>
				</h2>
				<p class="kova-v2-final__text">{{ text }}</p>
			</div>

			<!-- 6566:33085 — desktop only; on mobile the floating CTA carries the action. -->
			<LandingKovaV2Button
				class="kova-v2-final__cta"
				:label="ctaLabel"
				variant="section"
				@click="emit('progressLoaderCompleted', 'final_cta_eligible')"
			/>

			<!-- Three vials, back to front. Mobile 6438:61922 (322x248, with ground shadows);
			     desktop 6566:33105 (544x336, a second, larger pair of side vials). Both are
			     cut by the bottom edge, where the footer begins. -->
			<div aria-hidden="true" class="kova-v2-final__art">
				<span class="kova-v2-final__shadow kova-v2-final__shadow_right" />
				<span class="kova-v2-final__shadow kova-v2-final__shadow_left" />
				<img
					alt=""
					class="kova-v2-final__vial kova-v2-final__vial_back-right"
					height="918"
					decoding="async"
					loading="lazy"
					:src="sema"
					width="540"
				/>
				<img
					alt=""
					class="kova-v2-final__vial kova-v2-final__vial_back-left"
					height="918"
					decoding="async"
					loading="lazy"
					:src="tirz"
					width="540"
				/>
				<img
					alt=""
					class="kova-v2-final__vial kova-v2-final__vial_right"
					height="918"
					decoding="async"
					loading="lazy"
					:src="sema"
					width="540"
				/>
				<img
					alt=""
					class="kova-v2-final__vial kova-v2-final__vial_left"
					height="918"
					decoding="async"
					loading="lazy"
					:src="tirz"
					width="540"
				/>
				<span class="kova-v2-final__shadow kova-v2-final__shadow_center" />
				<img
					alt=""
					class="kova-v2-final__vial kova-v2-final__vial_center"
					height="919"
					decoding="async"
					loading="lazy"
					:src="glp1"
					width="540"
				/>
			</div>
		</section>
	</BlockVisibilityObserver>
</template>

<script setup lang="ts">
import BlockVisibilityObserver from '@/components/BlockVisibilityObserver.vue'
import LandingKovaV2Button from '@/modules/Landing/components/v2/LandingKovaV2Button.vue'
import { KovaV2CtaEntryPoint, LandingComponentProps } from '@/modules/Landing/instances/@types/landing-types'

import glp1 from '@/assets/kova/landing/v2/final-offer-vial-glp1.webp'
import sema from '@/assets/kova/landing/v2/final-offer-vial-semaglutide.webp'
import tirz from '@/assets/kova/landing/v2/final-offer-vial-tirzepatide.webp'

defineOptions({ name: 'LandingKovaV2FinalOffer' })

defineProps<LandingComponentProps.KovaV2FinalOffer>()

const emit = defineEmits<{ progressLoaderCompleted: [entryPoint: KovaV2CtaEntryPoint] }>()
</script>

<style lang="scss" scoped>
// Mobile (6426:54335): 20 top, 16px gutters, copy then the 322x248 art 24px below.
.kova-v2-final {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: var(--size-24);
	padding: var(--size-20) var(--size-16) 0;

	font-family: var(--kova-lp-font);
	text-align: center;
}

.kova-v2-final__copy {
	display: flex;
	flex-direction: column;
	gap: var(--size-12);
	width: 100%;
}

.kova-v2-final__title {
	margin: 0;

	font-size: var(--size-32);
	font-weight: 500;
	line-height: var(--size-36); // 32px × 1.12 = 35.84px — exact size not in design system
	letter-spacing: -0.64px;

	color: var(--kova-lp-ink);
}

.kova-v2-final__title-accent {
	color: var(--kova-lp-highlight);
}

.kova-v2-final__text {
	margin: 0;

	font-size: var(--size-16);
	line-height: var(--size-22); // 16px × 1.4 = 22.40px — exact size not in design system

	color: var(--kova-v2-text-secondary);
}

.kova-v2-final__cta {
	display: none;
}

.kova-v2-final__art {
	position: relative;
	overflow: hidden;
	flex-shrink: 0;

	width: 322px;
	max-width: 100%;
	height: 248px;

	img {
		position: absolute;
		display: block;
		max-width: none;
		height: auto;
	}
}

// Ground shadows (6438:61902 / 61905 / 61908): #323653 ellipses 8.76 high, layer blur 16
// — drawn as CSS ellipses blurred by the same Gaussian (stdDeviation 8).
.kova-v2-final__shadow {
	position: absolute;
	height: 8.76px;

	border-radius: 50%;
	background: var(--Text-Primary-100);
	filter: blur(8px); // Figma blur 16 = stdDeviation 8, fixed like the shadow it draws
	transform: translateX(-50%);
}

.kova-v2-final__shadow_left,
.kova-v2-final__shadow_right {
	bottom: 32.5px;
	width: 116.74px;
}

.kova-v2-final__shadow_left {
	left: calc(50% - 90.97px);
}

.kova-v2-final__shadow_right {
	left: calc(50% + 90.35px);
}

.kova-v2-final__shadow_center {
	left: calc(50% + 0.52px);
	bottom: 10.61px;
	width: 157.6px;
}

.kova-v2-final__art .kova-v2-final__vial_back-left,
.kova-v2-final__art .kova-v2-final__vial_back-right {
	display: none;
}

// 6438:61906 / 61903 — 141x240 side vials at (0,36) / (181,36).
.kova-v2-final__vial_left,
.kova-v2-final__vial_right {
	top: 36px;
	width: 141px;
}

.kova-v2-final__vial_left {
	left: calc(50% - 161px);
}

.kova-v2-final__vial_right {
	left: calc(50% + 20px);
}

// 6438:61909 — 176x300 centre box, cut 52px by the bottom edge.
.kova-v2-final__vial_center {
	top: 0;
	left: 50%;
	width: 176px;
	transform: translateX(-50%);
}

// Desktop (6557:33027): 60 top, 800px copy, the 397x90 CTA, 544x336 art; 48 apart.
@media (min-width: 1024px) {
	.kova-v2-final {
		gap: var(--size-48);
		padding: var(--size-60) var(--size-40) 0;
	}

	.kova-v2-final__copy {
		max-width: 800px;
	}

	.kova-v2-final__title {
		font-size: var(--size-48);
		line-height: var(--size-56); // 48px × 1.2 = 57.60px — exact size not in design system
		letter-spacing: -0.96px;
	}

	.kova-v2-final__text {
		font-size: var(--size-18);
		line-height: var(--size-24); // 18px × 1.4 = 25.20px — exact size not in design system
	}

	.kova-v2-final__cta {
		display: inline-flex;
	}

	.kova-v2-final__art {
		width: 544px;
		height: 336px;
	}

	.kova-v2-final__art .kova-v2-final__shadow {
		display: none;
	}

	// 6566:33106 — 172x292.6 back pair at (0,80) / (372,80).
	.kova-v2-final__art .kova-v2-final__vial_back-left,
	.kova-v2-final__art .kova-v2-final__vial_back-right {
		display: block;
		top: 80px;
		width: 172.08px;
	}

	.kova-v2-final__vial_back-left {
		left: calc(50% - 272px);
	}

	.kova-v2-final__vial_back-right {
		left: calc(50% + 100.08px);
	}

	// 6566:33111 — 210.6x358 front pair at (76,53.7) / (257,53.7).
	.kova-v2-final__vial_left,
	.kova-v2-final__vial_right {
		top: 53.71px;
		width: 210.6px;
	}

	.kova-v2-final__vial_left {
		left: calc(50% - 196px);
	}

	.kova-v2-final__vial_right {
		left: calc(50% - 14.98px);
	}

	// 6566:33117 — 263x447.6 centre box at (141,0).
	.kova-v2-final__vial_center {
		left: calc(50% - 131px);
		width: 263.05px;
		transform: none;
	}
}
</style>
